import React, { createContext, useContext, useMemo } from 'react';
import { useHttpClientCore, useHttpClientIntegration } from '@/shared';
import { FetchEmployeeRecordUseCase } from '../../application/use-cases/FetchEmployeeRecord';
import { FetchEVerifyCaseSummaryUseCase } from '../../application/use-cases/FetchEVerifyCaseSummaryUseCase';
import { ApiI9FormRecordRepository } from '../repositories/ApiEmployeeRecordRepository';
import { ApiEVerifyCaseSummaryRepository } from '../repositories/ApiEVerifyCaseSummaryRepository';
import { AmendSectionUseCase } from '../../application/use-cases/AmendSectionUseCase';
import { ApiH1bPetitionRepository } from '../repositories/ApiH1bPetitionRepository';
import type { IH1bPetitionRepository } from '../../application/ports/outbound/IH1bPetitionRepository';

interface EmployeeRecordDIContextType {
  fetchEmployeeRecordUseCase: FetchEmployeeRecordUseCase;
  fetchEVerifyCaseSummaryUseCase: FetchEVerifyCaseSummaryUseCase;
  amendSectionUseCase: AmendSectionUseCase;
  employeeRecordRepository: ApiI9FormRecordRepository;
  h1bPetitionRepository: IH1bPetitionRepository;
}

const EmployeeRecordDIContext = createContext<EmployeeRecordDIContextType | undefined>(undefined);

export const EmployeeRecordDIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const httpClientCore = useHttpClientCore();
  const httpClientIntegration = useHttpClientIntegration();

  const value = useMemo(() => {
    const employeeRecordRepository = new ApiI9FormRecordRepository(httpClientCore);
    const eVerifyCaseSummaryRepository = new ApiEVerifyCaseSummaryRepository(httpClientIntegration);

    const fetchEmployeeRecordUseCase = new FetchEmployeeRecordUseCase(employeeRecordRepository);
    const fetchEVerifyCaseSummaryUseCase = new FetchEVerifyCaseSummaryUseCase(
      eVerifyCaseSummaryRepository
    );

    const amendSectionUseCase = new AmendSectionUseCase(employeeRecordRepository);

    const h1bPetitionRepository = new ApiH1bPetitionRepository(httpClientCore);

    return {
      fetchEmployeeRecordUseCase,
      fetchEVerifyCaseSummaryUseCase,
      amendSectionUseCase,
      employeeRecordRepository,
      h1bPetitionRepository,
    };
  }, [httpClientCore, httpClientIntegration]);

  return (
    <EmployeeRecordDIContext.Provider value={value}>{children}</EmployeeRecordDIContext.Provider>
  );
};

export const useEmployeeRecordDIContext = (): EmployeeRecordDIContextType => {
  const context = useContext(EmployeeRecordDIContext);
  if (!context) {
    throw new Error('useEmployeeRecordDIContext must be used within EmployeeRecordDIProvider');
  }
  return context;
};
